import { useState, type MouseEvent } from "react";
import type { HealthBucket } from "../../hooks/useAdminStatus";
import {
  ChartTooltip,
  tooltipStyles as tip,
  type ChartTooltipAnchor,
} from "./chart/ChartTooltip";
import { bucketRangeLabel } from "../../lib/chartBuckets";
import styles from "./HealthStrip.module.css";

export const healthStateLabel = (state: string | null): string => {
  switch (state) {
    case "HEALTHY":
      return "Operational";
    case "DEGRADED":
      return "Degraded";
    case "DOWN":
      return "Down";
    case "UNREACHABLE":
      return "Unreachable";
    default:
      return "No data";
  }
};

const segColor = (state: HealthBucket["state"]): string => {
  switch (state) {
    case "HEALTHY":
      return styles.seg_HEALTHY ?? "";
    case "DEGRADED":
      return styles.seg_DEGRADED ?? "";
    case "DOWN":
      return styles.seg_DOWN ?? "";
    case "UNREACHABLE":
      return styles.seg_UNREACHABLE ?? "";
    default:
      return styles.seg_empty ?? "";
  }
};

const segmentClass = (state: HealthBucket["state"]): string => `${styles.segment} ${segColor(state)}`;

// Human labels for the bare operational reason codes. The richer
// degradation reasons (event-loop metrics, channel errors) are already
// human-readable from the backend and pass through unchanged.
const REASON_LABELS: Record<string, string> = {
  "openclaw-ok-false": "OpenClaw reported unhealthy",
  "health-probe-failed": "health probe failed",
  "rpc-failed": "gateway RPC not responding",
  "unit-inactive": "gateway service inactive",
  "port-not-listening": "gateway port not listening",
  "gateway-status-timeout": "gateway status timed out",
  "openclaw-missing": "OpenClaw binary missing",
  "gateway-status-failed": "gateway status check failed",
  transport: "unreachable over SSM",
  "probe-output-unparseable": "probe output unparseable",
  "probe-extractor-error": "probe extractor error",
};

const friendlyReason = (r: string): string => REASON_LABELS[r] ?? r;

// Screen-reader / no-pointer fallback for the visual hover card below.
const bucketAria = (bucket: HealthBucket, bucketSeconds: number): string => {
  const parts = [`${bucketRangeLabel(bucket.t, bucketSeconds)}: ${healthStateLabel(bucket.state)}`];
  for (const r of bucket.reasons) parts.push(friendlyReason(r));
  return parts.join("; ");
};

type Hover = ChartTooltipAnchor & { bucket: HealthBucket };

type HealthStripProps = {
  readonly buckets: ReadonlyArray<HealthBucket>;
  readonly bucketSeconds: number;
  // Taller bars for the detail modal.
  readonly tall?: boolean;
  // Shorter bars, no activity dots — inline in the Instances table.
  readonly compact?: boolean;
  // Render only the last N buckets (the recent-history slice in tight cells).
  readonly maxBuckets?: number;
};

export const HealthStrip = ({
  buckets,
  bucketSeconds,
  tall,
  compact,
  maxBuckets,
}: HealthStripProps): JSX.Element => {
  const [hover, setHover] = useState<Hover | null>(null);
  const shown = maxBuckets != null ? buckets.slice(-maxBuckets) : buckets;
  const cls = [styles.strip, tall ? styles.stripTall : "", compact ? styles.stripCompact : ""]
    .filter(Boolean)
    .join(" ");

  const onEnter = (bucket: HealthBucket) => (e: MouseEvent<HTMLSpanElement>): void => {
    const r = e.currentTarget.getBoundingClientRect();
    setHover({ bucket, cx: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  };

  return (
    <div className={cls}>
      {shown.map((bucket) => (
        <span
          key={bucket.t}
          className={segmentClass(bucket.state)}
          aria-label={bucketAria(bucket, bucketSeconds)}
          onMouseEnter={onEnter(bucket)}
          onMouseLeave={() => setHover(null)}
        />
      ))}
      {hover != null && (
        <ChartTooltip anchor={hover}>
          <div className={tip.tipHead}>
            <span className={`${tip.tipDot} ${segColor(hover.bucket.state)}`} aria-hidden />
            <span className={tip.tipState}>{healthStateLabel(hover.bucket.state)}</span>
            <span className={tip.tipTime}>{bucketRangeLabel(hover.bucket.t, bucketSeconds)}</span>
          </div>
          {hover.bucket.reasons.length > 0 && (
            <ul className={tip.tipReasons}>
              {hover.bucket.reasons.map((r, i) => (
                <li key={`${r}-${i}`}>{friendlyReason(r)}</li>
              ))}
            </ul>
          )}
        </ChartTooltip>
      )}
    </div>
  );
};
